import { useEffect, useState } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import { useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ProperGenreOrLanguage } from 'src/Types/tracks'
import {
  ANY_LANGUAGE_ID,
  DEBOUNCE_TIMER_300,
  SPOTIFY_BUDGET_STEP,
} from 'src/Constants/constants'
import { DIVIDE_BY_TWO } from 'src/Constants/numeric'
import { useScheduleEvents } from 'src/Hooks/useScheduleEvents'
import { useBudgetRange } from 'src/Redux/spotifyBudgetRangeApi'
import { useGetCurrencyInfoQuery } from 'src/Redux/userExchangeRateApi'
import {
  setCampaignBudget,
  spotifyTotalPriceSelector,
} from 'src/Redux/spotifyCampaignMatchingProcess'
import { useGetSpotifyCampaignMatchingQuery } from 'src/Redux/spotifyCampaignMatchingApi'

export const getIsLanguageActive = (
  languages: ProperGenreOrLanguage[],
): boolean => {
  const isEmpty = !languages.length
  const IsOnlyAnyLanguage =
    languages.some((language) => language.id === ANY_LANGUAGE_ID) &&
    languages.length === 1
  return Boolean(isEmpty || IsOnlyAnyLanguage)
}

interface UseCampaignPrice {
  savePrice: (value: number) => void
  min: number
  max: number
  valueInput: number
  valueSlider: number
  loading: boolean
  setValueInput: (value: number) => void
  setValueSlider: (value: number) => void
  currencySymbol: string
  rate: number
}

export const calculatePrice = (
  min: number,
  max: number,
  maxCuratorsCount: number | null,
  hasError: boolean,
): number => {
  if (maxCuratorsCount === 1) {
    return min
  }
  return hasError ? max : (min + max) / DIVIDE_BY_TWO
}

const clampToRange = (value: number, min: number, max: number): number => {
  if (value > max) {
    return max
  }
  if (value < min) {
    return min
  }
  return value
}

interface BudgetBounds {
  min: number
  max: number
  absoluteCuratorsCount: number | null
  isLowReachError: boolean
}

const useBudgetBounds = (): BudgetBounds => {
  const { isLowReachError, data } = useBudgetRange()
  return {
    isLowReachError,
    min: Number(data?.min?.toFixed(0) ?? 0),
    max: Math.ceil(data?.max ?? 0),
    absoluteCuratorsCount: data?.absoluteCuratorsCount ?? null,
  }
}

interface Currency {
  rate: number
  symbol: string
}

const useCurrency = (): Currency => {
  const { data } = useGetCurrencyInfoQuery()
  return {
    rate: Number(data?.currencyRate ?? 1),
    symbol: data?.currencySymbol ?? '$',
  }
}

const getIsOutOfRange = (value: number, min: number, max: number): boolean =>
  value !== 0 && (value < min || value > max)

const isBudgetWithinRange = (
  budget: number,
  min: number,
  max: number,
): boolean => min > 0 && budget >= min && budget <= max

const getShouldSyncToResponse = (
  isInitialLoad: boolean,
  isOutOfRange: boolean,
  numericBudget: number,
  min: number,
  max: number,
): boolean =>
  isInitialLoad ||
  (isOutOfRange && isBudgetWithinRange(numericBudget, min, max))

const useEmitBudgetEvent = (
  isSuccess: boolean,
  numericBudget: number,
): void => {
  const { handleBudgetEvents } = useScheduleEvents()
  useEffect(() => {
    if (!isSuccess || !numericBudget) {
      return
    }
    handleBudgetEvents({
      platformName: CampaignPlatformTypeEnum.SPOTIFY,
      resultPrice: numericBudget,
    })
  }, [isSuccess, numericBudget, handleBudgetEvents])
}

const useDispatchInitialBudget = (
  bounds: BudgetBounds,
  savePrice: (value: number) => void,
  existingTotalPrice: number | null,
): void => {
  const { min, max, absoluteCuratorsCount, isLowReachError } = bounds
  useEffect(() => {
    if (!min || !max || existingTotalPrice) {
      return
    }
    const price = calculatePrice(
      min,
      max,
      absoluteCuratorsCount,
      isLowReachError,
    )
    savePrice(price)
  }, [
    min,
    max,
    absoluteCuratorsCount,
    isLowReachError,
    savePrice,
    existingTotalPrice,
  ])
}

export const useCampaignPrice = (): UseCampaignPrice => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const campaignId = params.number
  const bounds = useBudgetBounds()
  const { rate, symbol } = useCurrency()
  const totalPrice = useAppSelector(spotifyTotalPriceSelector)

  const [valueInput, setValueInput] = useState(0)
  const [valueSlider, setValueSlider] = useState(0)

  const numericCampaignId = Number(campaignId)
  const numericBudget = Number(totalPrice)
  const skip = !campaignId || !totalPrice || !numericBudget

  const matchingResult = useGetSpotifyCampaignMatchingQuery(
    { campaignId: numericCampaignId, budget: numericBudget },
    { skip },
  )

  const loading =
    matchingResult.isFetching ||
    (matchingResult.data?.maxCuratorsCount ?? null) === null

  const savePrice = useDebouncedCallback((value: number) => {
    dispatch(setCampaignBudget(clampToRange(value, bounds.min, bounds.max)))
  }, DEBOUNCE_TIMER_300)

  const isInitialLoad = !valueSlider
  const isOutOfRange = getIsOutOfRange(valueSlider, bounds.min, bounds.max)
  const shouldSyncToResponse = getShouldSyncToResponse(
    isInitialLoad,
    isOutOfRange,
    numericBudget,
    bounds.min,
    bounds.max,
  )

  if (matchingResult.isSuccess && numericBudget && shouldSyncToResponse) {
    setValueInput(Math.round(numericBudget * rate))
    setValueSlider(numericBudget)
  }

  useEmitBudgetEvent(matchingResult.isSuccess, numericBudget)
  useDispatchInitialBudget(bounds, savePrice, totalPrice)

  return {
    max: bounds.max,
    min: bounds.min,
    currencySymbol: symbol,
    savePrice,
    valueInput,
    setValueInput,
    setValueSlider,
    valueSlider,
    loading,
    rate,
  }
}
export const roundPriceValue = (price: number | string): number =>
  Math.round(Number(price) / SPOTIFY_BUDGET_STEP) * SPOTIFY_BUDGET_STEP
